/*
 * File: components/payroll/PayrollDetails.js
 * Mô tả: Component hiển thị phiếu lương chi tiết (Giao diện Light Mode Bento).
 */
import React from 'react';

export default function PayrollDetails({ data, isAdminView = false }) {
    if (!data) return (
        <div className="bg-white rounded-3xl border border-gray-200 p-8 text-center shadow-sm">
            <span className="text-4xl mb-3 block">💸</span>
            <p className="text-gray-500 font-medium">Không có dữ liệu lương cho tháng này.</p>
        </div>
    );

    if (data.approved === false && !isAdminView) {
        return (
            <div className="bg-blue-50 border border-blue-100 rounded-3xl p-6 text-center shadow-sm">
                <span className="text-3xl mb-2 block">⏳</span>
                <p className="text-blue-700 font-medium">{data.message || 'Bảng lương đang chờ phê duyệt.'}</p>
            </div>
        );
    }

    const { employeeInfo = {}, earnings = [], deductions = [], overtimeAndBonus = [], summary = {} } = data;
    
    const formatCurrency = (amount) => {
        if (typeof amount !== 'number' || isNaN(amount)) return '0 ₫';
        return amount.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' });
    };

    const totalEarnings = earnings.reduce((sum, item) => sum + (item.value || 0), 0);
    const totalDeductions = deductions.reduce((sum, item) => sum + (item.value || 0), 0);
    const totalOvertimeAndBonus = overtimeAndBonus.reduce((sum, item) => sum + (item.amount || 0), 0);
    
    const DetailRow = ({ label, value, colorClass = 'text-gray-900', isBold = false }) => (
        <div className="flex justify-between items-center py-3 border-b border-gray-100 last:border-0">
            <p className="text-sm text-gray-500 font-medium">{label}</p>
            <p className={`text-sm ${isBold ? 'font-black' : 'font-bold'} ${colorClass}`}>{formatCurrency(value)}</p>
        </div>
    );

    // Xác định trạng thái và màu sắc
    const statusText = data.status || 'Đã duyệt'; // Mặc định là 'Đã duyệt' nếu không có dữ liệu status
    const isApproved = statusText.toLowerCase() === 'đã duyệt' || statusText.toLowerCase() === 'paid';
    const statusDotColor = isApproved ? 'bg-emerald-500' : 'bg-amber-500';
    const statusTextColor = isApproved ? 'text-gray-500' : 'text-amber-600';

    return (
        <div className="space-y-4">
            
            {/* Card Thực Nhận (Hero Card) */}
            <div className="bg-gradient-to-br from-emerald-50 via-white to-emerald-50 border border-emerald-100 rounded-[2rem] p-6 shadow-sm relative overflow-hidden">
                <div className="absolute -right-8 -top-8 w-32 h-32 bg-emerald-400/20 rounded-full blur-3xl pointer-events-none"></div>
                <div className="flex justify-between items-center mb-4">
                    <span className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-xs font-bold px-3 py-1 rounded-full">
                        Tháng {employeeInfo.thang || (new Date().getMonth() + 1)}, {employeeInfo.nam || new Date().getFullYear()}
                    </span>
                </div>
                <p className="text-sm text-gray-600 font-medium mb-1">Bảng lương chi tiết</p>
                <h2 className="text-4xl font-black text-emerald-600 tracking-tight">
                    {summary.luongThucLanh ? formatCurrency(summary.luongThucLanh) : 'Đang cập nhật'}
                </h2>
                
                <div className="mt-6 pt-4 border-t border-emerald-100/60 flex items-center justify-between text-sm">
                    <span className={`flex items-center gap-2 font-medium ${statusTextColor}`}>
                        <span className={`w-2 h-2 rounded-full ${statusDotColor}`}></span>
                        Trạng thái: {statusText}
                    </span>
                    <button className="text-emerald-700 font-bold hover:text-emerald-800 transition-colors flex items-center gap-1">
                        Chi tiết <span aria-hidden="true">&rarr;</span>
                    </button>
                </div>
            </div>

            {/* Thông tin nhân viên (Bento Info) */}
            <div className="bg-white border border-gray-200 rounded-3xl p-5 shadow-sm grid grid-cols-2 gap-4">
                <div>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">Nhân viên</p>
                    <p className="text-sm font-bold text-gray-900">{employeeInfo.hoTen || '---'}</p>
                </div>
                <div>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">Mã NV</p>
                    <p className="text-sm font-bold text-gray-900">{employeeInfo.soThe || '---'}</p>
                </div>
                <div>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">Chức vụ</p>
                    <p className="text-sm font-bold text-gray-900">{employeeInfo.chucVu || '---'}</p>
                </div>
                <div>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">Tiền công giờ</p>
                    <p className="text-sm font-bold text-gray-900">{formatCurrency(summary.tinhLuongMoiGio)}</p>
                </div>
            </div>

            {/* Khối Thu Nhập */}
            <div className="bg-white border border-gray-200 rounded-3xl p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                    <span className="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold">📈</span>
                    <h3 className="font-bold text-gray-900">Các Khoản Thu Nhập</h3>
                </div>
                <div className="space-y-1">
                    {earnings.map((item, index) => (
                        <DetailRow key={index} label={item.label} value={item.value} />
                    ))}
                    <div className="pt-2 mt-2 border-t border-gray-200">
                        <DetailRow label="Tổng Thu Nhập" value={totalEarnings} colorClass="text-blue-600" isBold={true} />
                    </div>
                </div>
            </div>

            {/* Khối Khấu Trừ */}
            <div className="bg-white border border-gray-200 rounded-3xl p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                    <span className="w-8 h-8 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center font-bold">📉</span>
                    <h3 className="font-bold text-gray-900">Các Khoản Khấu Trừ</h3>
                </div>
                <div className="space-y-1">
                    {deductions.map((item, index) => (
                        <DetailRow key={index} label={item.label} value={item.value} />
                    ))}
                    <div className="pt-2 mt-2 border-t border-gray-200">
                        <DetailRow label="Tổng Khấu Trừ" value={totalDeductions} colorClass="text-rose-600" isBold={true} />
                    </div>
                </div>
            </div>

            {/* Chi tiết Tăng ca & Thưởng */}
            {overtimeAndBonus && overtimeAndBonus.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-3xl p-5 shadow-sm overflow-hidden">
                    <div className="flex items-center gap-2 mb-4">
                        <span className="w-8 h-8 rounded-xl bg-amber-50 text-amber-500 flex items-center justify-center font-bold">✨</span>
                        <h3 className="font-bold text-gray-900">Tăng ca & Thưởng</h3>
                    </div>
                    <div className="overflow-x-auto -mx-5 px-5">
                        <table className="w-full text-left border-collapse whitespace-nowrap">
                            <thead>
                                <tr className="border-b border-gray-100">
                                    <th className="py-2 text-[11px] font-bold text-gray-400 uppercase">Hạng mục</th>
                                    <th className="py-2 text-[11px] font-bold text-gray-400 uppercase text-center">Giờ</th>
                                    <th className="py-2 text-[11px] font-bold text-gray-400 uppercase text-right">Thành tiền</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {overtimeAndBonus.map((item, index) => item.amount > 0 && (
                                    <tr key={index}>
                                        <td className="py-3 text-sm font-medium text-gray-700">{item.label}</td>
                                        <td className="py-3 text-sm font-bold text-gray-900 text-center">{item.hours}</td>
                                        <td className="py-3 text-sm font-bold text-emerald-600 text-right">{formatCurrency(item.amount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="border-t border-gray-200">
                                <tr>
                                    <td colSpan="2" className="py-3 text-right text-sm font-medium text-gray-500">Tổng cộng:</td>
                                    <td className="py-3 text-right text-sm font-black text-gray-900">{formatCurrency(totalOvertimeAndBonus)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}